import React from 'react';
import { Button } from '@mui/material';
import { getSelectorAttribute, stripeSyncStatusSelectors } from '@vy/app-selectors';
import { VyIcon, type VyIconProps } from '@vy/ui-design-system';
import { SyncStatus, SYNC_STATUSES } from '@vy/types';
import { isNotNullOrUndefined, isNotEmptyString, vyDate, withDefault, raiseUnreachable } from '@vy/utils';
import { formatDate } from '../../../../../utils/date';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { getStripeInvoiceDashboardLink } from '../../../../../services/integration-invoice/stripe-integration-invoice-link';
import { useGetNetSuiteDashboardUrl } from '../../../../../hooks/requests/use-get-net-suit-dashboard-url';
import { useGetPaddleTransactionUrl } from '../../../../../hooks/requests/use-get-paddle-transaction-url';
import { getQuickBooksInvoiceUrl } from '../../../../../services/integration-invoice/quick-books-integration-invoice-link';
import { getMorningInvoiceUrl } from '../../../../../services/integration-invoice/morning-integration-invoice-link';
import { getXeroInvoiceUrl } from '../../../../../services/integration-invoice/xero-integration-invoice-link';
import { toastFromPromise } from '../../../../../utils/vy-toasts';
import { VyTooltip } from '../../../../../components/new-design/VyTooltip';

type SyncServiceName =
  | typeof INTEGRATION_PROVIDERS.netSuite
  | typeof INTEGRATION_PROVIDERS.stripe
  | typeof INTEGRATION_PROVIDERS.quickBooks
  | typeof INTEGRATION_PROVIDERS.morning
  | typeof INTEGRATION_PROVIDERS.xero
  | typeof INTEGRATION_PROVIDERS.paddle;

type SyncStatusIconProps = {
  serviceName: SyncServiceName;
  syncStatus?: SyncStatus;
  syncStatusUpdatedAt?: Date;
  invoiceId?: string;
};

export const SyncStatusIcon: React.FC<SyncStatusIconProps> = ({
  serviceName,
  syncStatus,
  syncStatusUpdatedAt,
  invoiceId,
}) => {
  const status = withDefault(syncStatus, SYNC_STATUSES.None);
  const formattedDate = formatSyncStatusUpdatedAt(syncStatusUpdatedAt);
  const { refetch: fetchNetSuiteDashboardUrl } = useGetNetSuiteDashboardUrl(invoiceId ?? '', {
    enabled: false,
  });
  const { refetch: fetchPaddleTransactionUrl } = useGetPaddleTransactionUrl(invoiceId ?? '', {
    enabled: false,
  });

  const handleOpenIntegrationLink = async () => {
    switch (serviceName) {
      case INTEGRATION_PROVIDERS.netSuite: {
        await toastFromPromise(
          (async () => {
            const { data } = await fetchNetSuiteDashboardUrl({ throwOnError: true });
            const netSuiteUrl = data?.url;

            if (!isNotNullOrUndefined(netSuiteUrl)) {
              throw new Error('NetSuite invoice URL is unavailable.');
            }

            window.open(netSuiteUrl, '_blank');
          })(),
          {
            pending: 'Opening NetSuite invoice...',
            success: 'Opened',
            error: 'Failed to open NetSuite invoice',
          },
        );
        break;
      }
      case INTEGRATION_PROVIDERS.stripe: {
        const stripeInvoiceDashboardLink = getStripeInvoiceDashboardLink(invoiceId ?? '');
        if (stripeInvoiceDashboardLink) {
          window.open(stripeInvoiceDashboardLink, '_blank');
        }
        break;
      }
      case INTEGRATION_PROVIDERS.quickBooks: {
        const quickBooksInvoiceUrl = getQuickBooksInvoiceUrl(invoiceId ?? '');

        if (quickBooksInvoiceUrl) {
          window.open(quickBooksInvoiceUrl, '_blank');
        }
        break;
      }
      case INTEGRATION_PROVIDERS.morning: {
        const morningInvoiceUrl = getMorningInvoiceUrl();

        if (morningInvoiceUrl) {
          window.open(morningInvoiceUrl, '_blank');
        }
        break;
      }
      case INTEGRATION_PROVIDERS.xero: {
        const xeroInvoiceUrl = getXeroInvoiceUrl(invoiceId ?? '');

        if (xeroInvoiceUrl) {
          window.open(xeroInvoiceUrl, '_blank');
        }
        break;
      }
      case INTEGRATION_PROVIDERS.paddle: {
        await toastFromPromise(
          (async () => {
            const { data } = await fetchPaddleTransactionUrl({ throwOnError: true });
            const paddleUrl = data?.url;

            if (!isNotEmptyString(paddleUrl)) {
              throw new Error('Paddle transaction URL is unavailable.');
            }

            window.open(paddleUrl, '_blank');
          })(),
          {
            pending: 'Opening Paddle transaction...',
            success: 'Opened',
            error: 'Failed to open Paddle transaction',
          },
        );
        break;
      }
      default:
        break;
    }
  };

  return (
    <div
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      <VyTooltip tooltipContent={getSyncTooltipText(serviceName, status, formattedDate)} placement="top">
        <div
          style={{ cursor: status === SYNC_STATUSES.None ? 'default' : 'pointer' }}
          {...(serviceName === INTEGRATION_PROVIDERS.stripe
            ? getSelectorAttribute(stripeSyncStatusSelectors.icon)
            : {})}
        >
          <Button
            style={{
              padding: 0,
              minWidth: 0,
            }}
            onClick={() => handleOpenIntegrationLink()}
            disabled={status === SYNC_STATUSES.None}
          >
            <VyIcon
              strokeOpacity={0}
              icon={getIconName(serviceName, status)}
              className="flex items-center justify-center"
              color={getStatusColor(status)}
            />
          </Button>
        </div>
      </VyTooltip>
    </div>
  );
};

function getIconName(serviceName: SyncServiceName, status: SyncStatus) {
  switch (serviceName) {
    case INTEGRATION_PROVIDERS.netSuite:
      return getNetSuiteIconName(status);
    case INTEGRATION_PROVIDERS.stripe:
      return getStripeIconName(status);
    case INTEGRATION_PROVIDERS.quickBooks:
      return getQuickBooksIconName(status);
    case INTEGRATION_PROVIDERS.morning:
      return getMorningIconName(status);
    case INTEGRATION_PROVIDERS.xero:
      return getXeroIconName(status);
    case INTEGRATION_PROVIDERS.paddle:
      return getPaddleIconName(status);
    default:
      return raiseUnreachable(serviceName);
  }
}

function getSyncTooltipText(serviceName: string, syncStatus: SyncStatus, syncStatusUpdatedAt: string) {
  switch (syncStatus) {
    case SYNC_STATUSES.Synced:
      return (
        <>
          <span>●</span> {syncStatusUpdatedAt}
          <br />
          {serviceName} sync
        </>
      );
    case SYNC_STATUSES.Failed:
      return (
        <>
          <span>●</span> {serviceName} sync failed
        </>
      );
    case SYNC_STATUSES.None:
      return `${serviceName} is not synced`;
    default:
      return raiseUnreachable(syncStatus);
  }
}

function getNetSuiteIconName(status: SyncStatus): VyIconProps['icon'] {
  switch (status) {
    case SYNC_STATUSES.Synced:
      return 'SyncedErpIdle';
    case SYNC_STATUSES.Failed:
      return 'FailedErpIdle';
    case SYNC_STATUSES.None:
      return 'NotSyncedErpIdle';
    default:
      return raiseUnreachable(status);
  }
}

function getStripeIconName(status: SyncStatus): VyIconProps['icon'] {
  switch (status) {
    case SYNC_STATUSES.Synced:
      return 'SyncedStripeIdle';
    case SYNC_STATUSES.Failed:
      return 'FailedStripeDefault';
    case SYNC_STATUSES.None:
      return 'NotsyncedStripeIdle';
    default:
      return raiseUnreachable(status);
  }
}

function getQuickBooksIconName(status: SyncStatus): VyIconProps['icon'] {
  switch (status) {
    case SYNC_STATUSES.Synced:
      return 'QuickbooksSynced';
    case SYNC_STATUSES.Failed:
      return 'QuickbooksSyncFailed';
    case SYNC_STATUSES.None:
      return 'QuickbooksNotSynced';
    default:
      return raiseUnreachable(status);
  }
}

function getMorningIconName(status: SyncStatus): VyIconProps['icon'] {
  switch (status) {
    case SYNC_STATUSES.Synced:
      return 'MorningSynced';
    case SYNC_STATUSES.Failed:
      return 'MorningSyncFailed';
    case SYNC_STATUSES.None:
      return 'MorningNotSynced';
    default:
      return raiseUnreachable(status);
  }
}

function getXeroIconName(status: SyncStatus): VyIconProps['icon'] {
  switch (status) {
    case SYNC_STATUSES.Synced:
      return 'XeroSynced';
    case SYNC_STATUSES.Failed:
      return 'XeroSyncFailed';
    case SYNC_STATUSES.None:
      return 'XeroNotSynced';
    default:
      return raiseUnreachable(status);
  }
}

function getPaddleIconName(status: SyncStatus): VyIconProps['icon'] {
  switch (status) {
    case SYNC_STATUSES.Synced:
      return 'PaddleSynced';
    case SYNC_STATUSES.Failed:
      return 'PaddleSyncFailed';
    case SYNC_STATUSES.None:
      return 'PaddleNotSynced';
    default:
      return raiseUnreachable(status);
  }
}

function getStatusColor(status: SyncStatus, forTooltip = false): string | undefined {
  switch (status) {
    case SYNC_STATUSES.Synced:
      return '#8BC34A';
    case SYNC_STATUSES.Failed:
      return '#FF8A80';
    case SYNC_STATUSES.None:
      return undefined;
    default:
      if (forTooltip) {
        return 'inherit';
      }
      return raiseUnreachable(status);
  }
}

function formatSyncStatusUpdatedAt(date: Date | undefined) {
  return isNotNullOrUndefined(date) ? formatDate(vyDate(date), 'DD/MM HH:mm') : '';
}
